import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { CircularProgress, IconButton, Theme, Tooltip } from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as NavLink } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { isPayPerAgent } from '../../common/isPayPerAgent';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../common/useMakePHPRequest';
import {
  trackAddPersonalNumber,
  trackEditNumber,
} from '../../common/userTracking';
import { calendarState } from '../../recoil/atoms/calendarState';
import { Role, user } from '../../recoil/atoms/user';
import { getBusinessDids } from '../../recoil/selectors/getBusinessDids';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { AddAgentNumberModal } from '../components/modals/AddAgentNumberModal';
import { DeleteAgentNumberModal } from '../components/modals/DeleteAgentNumberModal';
import { EditNumberModal } from '../components/modals/EditNumberModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme: Theme & { custom: any }) => ({
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  actionButton: {
    whiteSpace: 'nowrap',
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& article': {
      margin: 0,
    },
    '& p': {
      lineHeight: 'normal',
      whiteSpace: 'normal',
      textTransform: 'uppercase',
    },
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  button: {
    marginRight: theme.spacing(1),
  },
}));

type PhoneNumberRecord = {
  DID: string;
  Numberid: number;
  entityid: number;
  external: string | null;
  id: number;
  type: 'user';
};

interface PhoneNumberCellProps {
  role: Role;
  onEdit: (
    row: PhoneNumberRecord,
    ref: React.RefObject<HTMLAnchorElement>
  ) => void;
}

const PhoneNumberCell: React.FC<GridRenderCellParams & PhoneNumberCellProps> = (
  params
) => {
  const { role, onEdit } = params;
  const isUser = role === Role.USER;
  const linkRef = useRef<HTMLAnchorElement>(null);
  return isUser ? (
    <>{params.value}</>
  ) : (
    <Tooltip title="Edit Number">
      <NavLink
        to="#"
        ref={linkRef}
        onClick={() => {
          onEdit(params.row, linkRef);
          trackEditNumber();
        }}
      >
        {params.value}
      </NavLink>
    </Tooltip>
  );
};

PhoneNumberCell.displayName = 'PhoneNumberCell';

// numbers in agent view
export function Numbers(props) {
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const setCaledendarDisplay = useSetRecoilState(calendarState);
  const nums = useRecoilValueLoadable(getBusinessDids);
  const data = {
    call: methodNameEnum.GET_BUSINESS_NUMBERS,
  };
  const businessNumbers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify(data),
    })
  );
  // state for opening/closing delete modal
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [selectedToggle, setSelectedToggle] = useState(false);
  // state for opening/closing add new modal
  const [showA, setShowA] = useState(false);
  const handleCloseA = () => setShowA(false);
  const handleShowA = () => {
    if (isPayPerAgent() || props?.userTier?.toLowerCase() === 'payperuse') {
      enqueueSnackbarWithStyles({
        message:
          'You do not have access to purchase a phone number. Please reach out to your account administrator to upgrade your access',
        alertProps: {
          severity: 'error',
        },
      });
    } else {
      setShowA(true);
    }
  };
  // state for delete number modal props
  // number to display = DID
  // id used to pass to delete number seelctor
  const numbersCached = useRef<any[]>([]);
  numbersCached.current = useMemo(
    () =>
      nums.state === 'hasValue'
        ? nums?.contents?.map((o) => ({
            ...o,
            id: o.Numberid,
          }))
        : numbersCached.current,
    [nums]
  );
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { canAddPhoneNumbers, role } = permissionsLoadable.contents;

  const [, setDeleteCancelTokenSource] = useState<any>();
  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);
  const [selected, setSelected] = useState<GridValidRowModel[]>([]);
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');
  const makeRequest = useMakePHPRequest({
    userAtom: userAtom.contents,
    setStatus: setDeleteStatus,
    setCancelTokenSource: setDeleteCancelTokenSource,
  });
  const setRefetchToggleForAgentNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DIDS)
  );
  const setRefetchToggleForNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_NUMBERS)
  );
  useEffect(() => {
    setCaledendarDisplay('none');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [businessSMSRulesLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_SMS_BY_BUSINESS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_SMS_BY_BUSINESS,
        apikey: userAtom?.contents?.apikey,
        businessid: userAtom?.contents?.businessid,
        businessId: userAtom?.contents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.reduce(
        (acc, item) => ({
          ...acc,
          nameLookup: {
            ...acc.nameLookup,
            [item.name]: item,
          },
        }),
        {
          nameLookup: {},
        }
      ) ?? cached.current
  );
  const loginUser = userAtom.contents?.user ?? {};
  function actionsFormatter(params) {
    return deleteGroupId === params.row.id ? (
      <div className={classes.actionButton}>
        <article>
          <Tooltip title="Confirm Deletion">
            <>
              <IconButton
                aria-label="accept"
                onClick={async (event) => {
                  event.preventDefault(); // stop link
                  // insert action to delete here and refetch toggle
                  const res = await makeRequest({
                    methodName: methodNameEnum.DELETE_NUMBER,
                    payload: {
                      role: userAtom.contents.user.role,
                      numberid: params.row.id,
                    },
                  });
                  if (!res?.response?.data?.meta?.success) {
                    enqueueSnackbarWithStyles({
                      message: 'Could not delete this number',
                      alertProps: { severity: 'error' },
                    });
                  } else {
                    enqueueSnackbarWithStyles({
                      message: 'Successfully Deleted Number',
                      alertProps: { severity: 'success' },
                    });
                  }
                  setSelectedToggle((prev) => !prev);
                  setRefetchToggleForAgentNumbers({});
                  setDeleteGroupId(undefined);
                }}
                disabled={deleteStatus === loadableEnum.LOADING}
                size="large"
              >
                <CheckIcon
                  color={
                    deleteStatus === loadableEnum.LOADING
                      ? 'disabled'
                      : 'primary'
                  }
                />
                {deleteStatus === loadableEnum.LOADING && (
                  <CircularProgress
                    size={43}
                    className={classes.buttonProgress}
                  />
                )}
              </IconButton>
            </>
          </Tooltip>
          <Tooltip title="Cancel">
            <>
              <IconButton
                aria-label="cancel"
                onClick={(event) => {
                  event.preventDefault(); // stop link
                  // reset groupid hide confirm / cancel deletion
                  setDeleteGroupId(undefined);
                }}
                disabled={deleteStatus === loadableEnum.LOADING}
                size="large"
              >
                <ClearIcon
                  color="disabled"
                  className={clsx({
                    [classes.deleteIconButton]:
                      deleteStatus !== loadableEnum.LOADING, // || deleteCancelTokenSource,
                  })}
                />
              </IconButton>
            </>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div className={classes.actionButton}>
        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => setDeleteGroupId(params.row.id)}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const [editModal, handleEditModal] = useState(false);
  const [rowForEdit, handleRowForEdit] = useState({});
  const editModalStateRef = useRef<any>();
  editModalStateRef.current = editModal;
  const linkStateRef = useRef<any>();
  const businessNumbersStateRef = useRef<any>();
  businessNumbersStateRef.current = businessNumbers;
  function handleEdit(row, linkRef) {
    linkStateRef.current = linkRef?.current;
    const editNumber = businessNumbers.contents?.response?.data?.result?.find(
      (el) => el.Numberid === row.Numberid
    );
    if (editNumber) {
      handleEditModal(true);
      handleRowForEdit(editNumber);
    } else if (typeof row.Numberid === 'number') {
      const tryClick = () => {
        if (editModalStateRef.current) {
          return;
        }
        if (businessNumbersStateRef.current.state !== 'loading') {
          setRefetchToggleForNumbers({});
        }
        setTimeout(() => {
          linkStateRef?.current?.click?.();
          setTimeout(() => tryClick, 1000);
        }, 500);
      };
      tryClick();
    }
  }

  const createPhoneNumberCell = (
    role: Role,
    onEdit: (
      row: PhoneNumberRecord,
      ref: React.RefObject<HTMLAnchorElement>
    ) => void
  ) => {
    const CellRenderer = (params: GridRenderCellParams) => (
      <PhoneNumberCell {...params} role={role} onEdit={onEdit} />
    );

    CellRenderer.displayName = 'PhoneNumberCellRenderer';

    return CellRenderer;
  };

  const columns = [
    {
      field: 'DID',
      headerName: 'Number',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      // sortComparator: (v1, v2, row1, row2) => row2.data.DID - row1.DID,
      renderCell: createPhoneNumberCell(role, handleEdit),
    },
    {
      field: 'Numberid',
      hide: !canAddPhoneNumbers,
      headerName: 'Actions',
      headerAlign: 'center',
      width: 120,
      enableSorting: false,
      renderCell: actionsFormatter,
    },
  ] /* satisfies */ as IColDef[];
  const sortModel: GridSortModel = [
    {
      field: 'DID',
      sort: 'desc',
    },
  ];
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Numbers"
        numSelected={selected.length}
        rightComponent={
          canAddPhoneNumbers && (
            <ToolbarAddDeleteButton
              numSelected={selected.length}
              onClickForAdd={() => {
                handleShowA();
                if (
                  window.location.pathname ===
                  `/my-profile/${loginUser.userid}/numbers`
                ) {
                  trackAddPersonalNumber();
                }
              }}
              onClickForDelete={() => {
                handleShow();
              }}
              isLoading={props.isLoading}
            />
          )
        }
      />
    ),
    [
      handleShowA,
      handleShow,
      selected.length,
      canAddPhoneNumbers,
      loginUser.userid,
    ]
  );

  return (
    <>
      <DataTable
        enableSelectAll={true}
        setSelected={canAddPhoneNumbers ? setSelected : undefined}
        rows={numbersCached?.current ?? []}
        loading={nums.state === 'loading'}
        columns={columns}
        sortModel={sortModel}
        setSelectedToggle={setSelectedToggle}
        selectedToggle={selectedToggle}
        ToolbarComponent={ToolbarComponent}
        selected={selected}
      />
      <DeleteAgentNumberModal
        handleClose={handleClose}
        setSelected={setSelected}
        setSelectedToggle={setSelectedToggle}
        // handleShow={handleShow}
        show={show}
        selected={selected}
        userAtom={userAtom}
        setUserAtom={setUserAtom}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        setRefetchToggleForAgentNumbers={setRefetchToggleForAgentNumbers}
      />
      <AddAgentNumberModal
        handleClose={handleCloseA}
        handleShow={handleShowA}
        show={showA}
        setRefetchToggleForNumbers={setRefetchToggleForNumbers}
      />
      <EditNumberModal
        show={!!editModal}
        handleClose={handleEditModal}
        methodNameEnum={methodNameEnum}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        number={rowForEdit}
        refetchBusinessNumbers={setRefetchToggleForNumbers}
        businessSMSRulesLoadable={businessSMSRulesLoadable}
      />
    </>
  );
}
